import { useState, useCallback, useEffect, createContext, useContext } from "react";

export type LayoutMode = "compact" | "standard" | "wide" | "full";
export type FontSize = "small" | "medium" | "large";

const LAYOUT_KEY = "sdf-layout-mode";
const FONT_KEY = "sdf-font-size";

const LAYOUT_CLASSES: Record<LayoutMode, string> = {
  compact: "max-w-5xl",
  standard: "max-w-7xl",
  wide: "max-w-[1600px]",
  full: "max-w-full",
};

const LAYOUT_LABELS: Record<LayoutMode, string> = {
  compact: "Compact",
  standard: "Standard",
  wide: "Wide",
  full: "Full Width",
};

const ALL_MODES: LayoutMode[] = ["compact", "standard", "wide", "full"];

// Font sizing is driven by a `data-font-size` attribute on <html>, NOT by
// inline html font-size. The base layer in client/src/index.css sets
// `html { font-size: 14px }` which is the "small" baseline; index.css then
// has matching `html[data-font-size="medium"]` and `html[data-font-size="large"]`
// rules that bump the root font size, which in turn scales every rem-based
// Tailwind utility (text-xs/sm/base/lg/xl/2xl, p-/m-/gap-, etc.). Hard-
// coded px font sizes scattered through the app (text-[10px], inline
// style={{fontSize:'13px'}}, etc.) will not scale — that's known and is
// the trade-off vs. a much larger refactor.
const FONT_LABELS: Record<FontSize, string> = {
  small: "Small",
  medium: "Medium",
  large: "Large",
};

const ALL_FONT_SIZES: FontSize[] = ["small", "medium", "large"];

function getStoredLayout(): LayoutMode {
  try {
    const stored = localStorage.getItem(LAYOUT_KEY);
    if (stored && ALL_MODES.includes(stored as LayoutMode)) {
      return stored as LayoutMode;
    }
  } catch {}
  return "standard";
}

function getStoredFont(): FontSize {
  try {
    const stored = localStorage.getItem(FONT_KEY);
    if (stored && ALL_FONT_SIZES.includes(stored as FontSize)) {
      return stored as FontSize;
    }
  } catch {}
  return "medium";
}

interface LayoutContextValue {
  mode: LayoutMode;
  setMode: (m: LayoutMode) => void;
  containerClass: string;
  label: string;
  allModes: LayoutMode[];
  labels: Record<LayoutMode, string>;
  fontSize: FontSize;
  setFontSize: (f: FontSize) => void;
  fontLabel: string;
  allFontSizes: FontSize[];
  fontLabels: Record<FontSize, string>;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function useLayoutProvider() {
  const [mode, setModeState] = useState<LayoutMode>(getStoredLayout);
  const [fontSize, setFontState] = useState<FontSize>(getStoredFont);

  const setMode = useCallback((m: LayoutMode) => {
    setModeState(m);
    try { localStorage.setItem(LAYOUT_KEY, m); } catch {}
  }, []);

  const setFontSize = useCallback((f: FontSize) => {
    setFontState(f);
    try { localStorage.setItem(FONT_KEY, f); } catch {}
  }, []);

  useEffect(() => {
    // Clear any legacy inline font-size set by older versions of this hook
    // so the data-attribute / stylesheet rules win cleanly.
    document.documentElement.style.removeProperty("font-size");
    document.documentElement.setAttribute("data-font-size", fontSize);
  }, [fontSize]);

  return {
    mode,
    setMode,
    containerClass: LAYOUT_CLASSES[mode],
    label: LAYOUT_LABELS[mode],
    allModes: ALL_MODES,
    labels: LAYOUT_LABELS,
    fontSize,
    setFontSize,
    fontLabel: FONT_LABELS[fontSize],
    allFontSizes: ALL_FONT_SIZES,
    fontLabels: FONT_LABELS,
  };
}

export { LayoutContext };

export function useLayout() {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    return {
      mode: "standard" as LayoutMode,
      setMode: () => {},
      containerClass: LAYOUT_CLASSES.standard,
      label: LAYOUT_LABELS.standard,
      allModes: ALL_MODES,
      labels: LAYOUT_LABELS,
      fontSize: "medium" as FontSize,
      setFontSize: () => {},
      fontLabel: FONT_LABELS.medium,
      allFontSizes: ALL_FONT_SIZES,
      fontLabels: FONT_LABELS,
    };
  }
  return ctx;
}
